<template>
  <button
    class="v2-btn"
    :class="[
      `v2-btn--${variant}`,
      `v2-btn--${size}`,
      { 'v2-btn--block': block, 'v2-btn--loading': loading },
    ]"
    :disabled="disabled || loading"
    :type="type"
    v-on="$listeners"
  >
    <span
      v-if="loading"
      class="v2-btn__spinner"
    />
    <slot />
  </button>
</template>

<script>
export default {
  name: 'V2Button',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'outline', 'ghost', 'destructive', 'link'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    block: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    type: { type: String, default: 'button' },
  },
}
</script>

<style lang="scss" scoped>
.v2-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2-space-2);
  font-family: var(--v2-font-sans);
  font-weight: 500;
  border: none;
  border-radius: var(--v2-radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // Sizes
  &--sm { padding: var(--v2-space-1) var(--v2-space-3); font-size: 0.875rem; }
  &--md { padding: var(--v2-space-2) var(--v2-space-4); font-size: 0.875rem; }
  &--lg { padding: var(--v2-space-3) var(--v2-space-6); font-size: 1rem; }

  // Variants
  // primary / secondary / destructive stay solid for legibility — small
  // CTAs against a busy mesh read poorly when fully translucent. Glass
  // treatment is applied to outline + ghost where it adds depth without
  // hurting affordance.
  &--primary {
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    &:hover:not(:disabled) { filter: brightness(1.1); }
  }
  &--secondary {
    background: var(--v2-secondary);
    color: var(--v2-secondary-foreground);
    &:hover:not(:disabled) { filter: brightness(0.95); }
  }
  // Outline + ghost — no `backdrop-filter`. Buttons render dozens-to-
  // hundreds of times per page; per-button blur creates significant GPU
  // compositing pressure. Translucent fill + glass-stroke border read as
  // glass over the page mesh substrate without per-element blur.
  &--outline {
    background: var(--v2-glass-surface-2);
    color: var(--v2-foreground);
    border: 1px solid var(--v2-glass-stroke);
    &:hover:not(:disabled) {
      background: var(--v2-glass-surface-1);
      border-color: var(--v2-glass-stroke-strong);
    }
  }
  &--ghost {
    background: transparent;
    color: var(--v2-foreground);
    &:hover:not(:disabled) {
      background: var(--v2-glass-surface-2);
    }
  }
  &--destructive {
    background: var(--v2-destructive);
    color: #fff;
    &:hover:not(:disabled) { filter: brightness(1.1); }
  }
  &--link {
    background: none;
    color: var(--v2-link-primary);
    padding: 0;
    &:hover:not(:disabled) { text-decoration: underline; }
  }

  &--block { width: 100%; }

  &__spinner {
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: v2-spin 0.6s linear infinite;
  }
}

@keyframes v2-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-transparency: reduce) {
  .v2-btn--outline {
    background: var(--v2-background);
    border-color: var(--v2-border);
    &:hover:not(:disabled) { background: var(--v2-muted); }
  }
  .v2-btn--ghost:hover:not(:disabled) {
    background: var(--v2-muted);
  }
}
</style>
